import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Modal,
  Typography,
  Chip,
  Skeleton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Videocam as VideocamIcon,
  SkipPrevious as SkipPreviousIcon,
  SkipNext as SkipNextIcon,
} from "@mui/icons-material";
import { backend_url } from "../App";

export default function CameraFeed(props) {
  const theme = useTheme();
  const cameraId = props.cameraId;
  const [imageUrls, setImageUrls] = useState(props.imageUrls || []);
  const [loading, setLoading] = useState(true);
  const [isPaused, setPaused] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timerInterval] = useState(500);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventGifUrl, setEventGifUrl] = useState(null);
  const [showGif, setShowGif] = useState(true);
  const scrollContainerRef = useRef(null);
  const timerIdRef = useRef(null);

  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  const checkOverflow = () => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      setIsOverflow(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    }
  };

  const pauseGif = useCallback(() => {
    setPaused(true);
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  const playGif = useCallback(() => {
    if (imageUrls.length === 0) return;
    setPaused(false);
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
    }
    const newTimerId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    }, timerInterval);
    timerIdRef.current = newTimerId;
  }, [imageUrls.length, timerInterval]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      playGif();
    } else {
      pauseGif();
    }
  }, [isPaused, playGif, pauseGif]);

  // Update imageUrls when props change
  useEffect(() => {
    setImageUrls(props.imageUrls || []);
    if (props.imageUrls.length > 0) {
      setCurrentImageIndex(0);
      // Auto-play when new images arrive only if in frame view
      if (!showGif) {
        playGif();
      }
    }
    // Cleanup timer on unmount or when imageUrls change
    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, [props.imageUrls, showGif, playGif]);

  // Set event GIF URL from props
  useEffect(() => {
    if (cameraId.link) {
      let gifUrl = cameraId.link;
      if (gifUrl.includes("http://127.0.0.1:8000")) {
        gifUrl = gifUrl.replace(
          "http://127.0.0.1:8000",
          "https://api.forestwatch.org.pk",
        );
      }
      setEventGifUrl(gifUrl);
    }
  }, [cameraId.link]);

  // Handle GIF autoplay based on pause state (only for frame view)
  useEffect(() => {
    // Clear any existing timer
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }

    // Only auto-play in frame view (not GIF view)
    if (!showGif && !isPaused && imageUrls.length > 0) {
      const newTimerId = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
      }, timerInterval);
      timerIdRef.current = newTimerId;
    }

    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, [isPaused, imageUrls.length, timerInterval, showGif]);

  useEffect(() => {
    checkOverflow();
  }, [imageUrls]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft -= 100;
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 100;
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    setShowGif(false);
    pauseGif();
  };

  const handlePrevFrame = () => {
    const prevIndex =
      (currentImageIndex - 1 + imageUrls.length) % imageUrls.length;
    setCurrentImageIndex(prevIndex);
    setShowGif(false);
    pauseGif();
  };

  const handleNextFrame = () => {
    const nextIndex = (currentImageIndex + 1) % imageUrls.length;
    setCurrentImageIndex(nextIndex);
    setShowGif(false);
    pauseGif();
  };

  const toggleViewMode = () => {
    const newShowGif = !showGif;
    setShowGif(newShowGif);
    if (newShowGif) {
      // Switching to GIF view - pause frame animation
      pauseGif();
    } else {
      // Switching to frame view - auto-play
      playGif();
    }
  };

  // Fetch camera data on mount
  useEffect(() => {
    const fetchCameraData = async () => {
      let fetchUrl = "";

      if (cameraId.link !== "") {
        setLoading(false);
        return;
      } else if (cameraId.cam === "Event") {
        setLoading(false);
        return;
      } else {
        fetchUrl = `${backend_url}/core/api/camera/${cameraId.id}`;
      }

      try {
        const response = await fetch(fetchUrl, config);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        // const actualData = await response.json();
        // let imageLink = actualData.live_image;
        // We don't need to set picture as it's not used
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCameraData();
  }, [cameraId, config]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    bgcolor: "background.paper",
    borderRadius: 4,
    boxShadow: 24,
    p: 3,
    outline: "none",
  };

  const hasImages = imageUrls.length > 0;

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ borderRadius: 3 }}
          />
        </Box>
      ) : (
        <Box>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={<VideocamIcon sx={{ fontSize: 16 }} />}
                label={cameraId.cam || "Event"}
                size="small"
                color="primary"
                sx={{ fontWeight: 600, color: "#fff" }}
              />
              {hasImages && (
                <Chip
                  label={`${currentImageIndex + 1}/${imageUrls.length}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    color: "#fff",
                    fontWeight: 500,
                  }}
                />
              )}
              {eventGifUrl && (
                <Chip
                  label={showGif ? "🎬 GIF View" : "📷 Frame View"}
                  size="small"
                  onClick={toggleViewMode}
                  sx={{
                    cursor: "pointer",
                    bgcolor: showGif
                      ? alpha("#4caf50", 0.3)
                      : alpha("#2196f3", 0.3),
                    color: "#fff",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                />
              )}
            </Box>
            {/* Play/Pause controls - only show in Frame View */}
            {!showGif && hasImages && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={isPaused ? "Play" : "Pause"}>
                  <IconButton
                    size="small"
                    onClick={togglePause}
                    sx={{
                      bgcolor: alpha("#fff", 0.15),
                      color: "#fff",
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.25),
                      },
                    }}
                  >
                    {isPaused ? (
                      <PlayArrowIcon fontSize="small" />
                    ) : (
                      <PauseIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Chip
                  size="small"
                  label={isPaused ? "⏸ Paused" : "▶ Playing"}
                  color={isPaused ? "warning" : "success"}
                  onClick={togglePause}
                  sx={{
                    height: 24,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    color: "#fff",
                  }}
                />
              </Box>
            )}
            {/* Show frame count in GIF view */}
            {showGif && eventGifUrl && (
              <Chip
                size="small"
                label="▶ Auto-Playing"
                color="success"
                sx={{
                  height: 24,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  color: "#fff",
                }}
              />
            )}
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 2 }}>
            <Box>
              <Box sx={{ position: "relative" }}>
                {showGif && eventGifUrl ? (
                  // Show the actual GIF (no play/pause, just auto-playing)
                  <img
                    src={eventGifUrl}
                    alt="Event GIF"
                    style={{
                      width: "100%",
                      height: 470,
                      borderRadius: 16,
                      objectFit: "contain",
                      backgroundColor: "#000",
                    }}
                    onError={(e) => {
                      console.error("Failed to load GIF:", eventGifUrl);
                      setShowGif(false);
                    }}
                  />
                ) : hasImages ? (
                  // Show individual frame
                  <img
                    src={imageUrls[currentImageIndex]}
                    alt={`Frame ${currentImageIndex + 1}`}
                    style={{
                      width: "100%",
                      height: 360,
                      borderRadius: 16,
                      objectFit: "contain",
                      backgroundColor: "#000",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: 400,
                      borderRadius: 16,
                      backgroundColor: "#000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#666" }}>
                      No images available
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#444" }}>
                      Select an event to view frames
                    </Typography>
                  </Box>
                )}

                <Tooltip title="Fullscreen">
                  <IconButton
                    onClick={() => setIsModalOpen(true)}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      bgcolor: alpha("#000", 0.7),
                      color: "#fff",
                      "&:hover": {
                        bgcolor: alpha("#000", 0.9),
                      },
                    }}
                  >
                    <FullscreenIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Navigation Controls - Frame View */}
              {hasImages && !showGif && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Tooltip title="Previous Frame">
                    <IconButton
                      size="small"
                      onClick={handlePrevFrame}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: "#fff",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.25),
                        },
                      }}
                    >
                      <SkipPreviousIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isPaused ? "Play" : "Pause"}>
                    <IconButton
                      size="medium"
                      onClick={togglePause}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: "#fff",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.3),
                        },
                      }}
                    >
                      {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Next Frame">
                    <IconButton
                      size="small"
                      onClick={handleNextFrame}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: "#fff",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.25),
                        },
                      }}
                    >
                      <SkipNextIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Switch to GIF View">
                    <IconButton
                      size="small"
                      onClick={toggleViewMode}
                      sx={{
                        bgcolor: alpha("#4caf50", 0.2),
                        color: "#fff",
                        "&:hover": {
                          bgcolor: alpha("#4caf50", 0.3),
                        },
                      }}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Switch to Frames button - GIF View */}
              {showGif && eventGifUrl && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Tooltip title="View Individual Frames">
                    <IconButton
                      size="small"
                      onClick={toggleViewMode}
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.2),
                        color: "#fff",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.info.main, 0.3),
                        },
                      }}
                    >
                      <SkipNextIcon fontSize="small" />
                      <Typography
                        variant="caption"
                        sx={{ ml: 1, color: "#fff" }}
                      >
                        View Frames
                      </Typography>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Thumbnail Strip - Frame View */}
              {imageUrls.length > 0 && !showGif && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isOverflow && (
                      <IconButton
                        size="small"
                        onClick={scrollLeft}
                        sx={{ color: "#fff" }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                    )}
                    <Box
                      ref={scrollContainerRef}
                      sx={{
                        display: "flex",
                        overflowX: "auto",
                        gap: 1,
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": {
                          height: 6,
                        },
                        "&::-webkit-scrollbar-track": {
                          bgcolor: alpha(theme.palette.grey[500], 0.2),
                          borderRadius: 3,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: alpha(theme.palette.primary.main, 0.5),
                          borderRadius: 3,
                        },
                        flex: 1,
                        py: 0.5,
                      }}
                    >
                      {imageUrls.map((imageUrl, index) => (
                        <Box
                          key={index}
                          onClick={() => handleThumbnailClick(index)}
                          sx={{
                            flexShrink: 0,
                            cursor: "pointer",
                            borderRadius: 2,
                            overflow: "hidden",
                            border:
                              index === currentImageIndex
                                ? `2px solid ${theme.palette.primary.main}`
                                : `2px solid transparent`,
                            transition: "all 0.2s ease",
                            position: "relative",
                            "&:hover": {
                              opacity: 0.8,
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Frame ${index + 1}`}
                            style={{
                              width: 80,
                              height: 60,
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 2,
                              right: 2,
                              bgcolor: "rgba(0,0,0,0.7)",
                              color: "white",
                              fontSize: "8px",
                              px: 0.5,
                              borderRadius: 1,
                              fontFamily: "monospace",
                            }}
                          >
                            {index + 1}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {isOverflow && (
                      <IconButton
                        size="small"
                        onClick={scrollRight}
                        sx={{ color: "#fff" }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Fullscreen Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              bgcolor: "transparent",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              {showGif ? "🎬 Event GIF" : "📷 Event Frames"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!showGif && hasImages && (
                <Tooltip title={isPaused ? "Play" : "Pause"}>
                  <IconButton
                    onClick={togglePause}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                  </IconButton>
                </Tooltip>
              )}
              {eventGifUrl && (
                <Chip
                  label={showGif ? "🎬 GIF" : "📷 Frames"}
                  size="small"
                  onClick={toggleViewMode}
                  sx={{
                    cursor: "pointer",
                    bgcolor: showGif
                      ? alpha("#4caf50", 0.2)
                      : alpha("#2196f3", 0.2),
                  }}
                />
              )}
              <IconButton onClick={() => setIsModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ position: "relative" }}>
            {showGif && eventGifUrl ? (
              <img
                src={eventGifUrl}
                alt="Event GIF"
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: 16,
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              />
            ) : imageUrls.length > 0 ? (
              <img
                src={imageUrls[currentImageIndex]}
                alt={`Frame ${currentImageIndex + 1}`}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: 16,
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#000",
                }}
              >
                <Typography variant="body1" sx={{ color: "#666" }}>
                  No media available
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            {!showGif && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Tooltip title="Previous Frame">
                  <IconButton
                    onClick={handlePrevFrame}
                    disabled={imageUrls.length === 0}
                  >
                    <SkipPreviousIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isPaused ? "Play" : "Pause"}>
                  <IconButton
                    onClick={togglePause}
                    disabled={imageUrls.length === 0}
                  >
                    {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Next Frame">
                  <IconButton
                    onClick={handleNextFrame}
                    disabled={imageUrls.length === 0}
                  >
                    <SkipNextIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
